import React, { useEffect, useState, useContext } from 'react';
import { useSubSite } from 'contexts/SubSiteContext';
import { AccessibilityContext } from 'contexts/AccessibilityContext';
import useVilleDeNoelSettings from 'hooks/useVilleDeNoelSettings';
import './VilleDeNoelEffects.css';

/**
 * Composant pour gérer les effets spéciaux de Ville de Noël
 */
const VilleDeNoelEffects = () => {
  const { isSubSite, subSiteSlug } = useSubSite();
  const { animationsEnabled } = useContext(AccessibilityContext);
  const { 
    isActive, 
    hasSnowEffect, 
    hasChristmasMusic, 
    christmasMessage,
    loading,
    error
  } = useVilleDeNoelSettings();
  
  const [showMessage, setShowMessage] = useState(false);
  const [musicPlaying, setMusicPlaying] = useState(false);

  // Si l'API échoue, utiliser des valeurs par défaut pour les tests
  const effectiveSettings = error ? {
    isActive: true,
    hasSnowEffect: true, // Activer par défaut pour les tests
    hasChristmasMusic: false,
    christmasMessage: "🎄 Joyeux Noël ! 🎄"
  } : {
    isActive,
    hasSnowEffect,
    hasChristmasMusic,
    christmasMessage
  };

  // Afficher le message au chargement
  useEffect(() => {
    if (effectiveSettings.christmasMessage) {
      const timer = setTimeout(() => {
        setShowMessage(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [effectiveSettings.christmasMessage]);

  // Afficher seulement si on est sur le sous-site ville-de-noel
  if (!isSubSite || subSiteSlug !== 'ville-de-noel' || loading || !effectiveSettings.isActive) {
    return null;
  }

  // Effet neige
  const SnowEffect = () => {
    if (!effectiveSettings.hasSnowEffect || !animationsEnabled) return null;

    const snowflakes = Array.from({ length: 50 }, (_, i) => (
      <div
        key={i}
        className="snowflake"
        style={{
          left: `${Math.random() * 100}%`,
          animationDelay: `${Math.random() * 3}s`,
          animationDuration: `${3 + Math.random() * 2}s`
        }}
      >
        ❄
      </div>
    ));

    return <div className="snow-container">{snowflakes}</div>;
  };

  // Message de Noël
  const ChristmasMessage = () => {
    if (!effectiveSettings.christmasMessage) return null;

    return (
      <div className={`christmas-message ${showMessage ? 'show' : ''}`}>
        <div className="christmas-message-content">
          <button 
            className="christmas-message-close"
            onClick={() => setShowMessage(false)}
            aria-label="Fermer le message"
          >
            ×
          </button>
          <div className="christmas-message-text">
            🎄 {effectiveSettings.christmasMessage} 🎄
          </div>
        </div>
      </div>
    );
  };

  // Contrôleur de musique
  const MusicController = () => {
    if (!effectiveSettings.hasChristmasMusic) return null;

    const toggleMusic = () => {
      // Ici vous pourriez implémenter la logique pour jouer/arrêter la musique
      setMusicPlaying(!musicPlaying);
      console.log(musicPlaying ? 'Arrêt de la musique' : 'Lecture de la musique');
    };

    return (
      <div className="music-controller">
        <button 
          className={`music-toggle ${musicPlaying ? 'playing' : ''}`}
          onClick={toggleMusic}
          title={musicPlaying ? 'Arrêter la musique' : 'Jouer la musique de Noël'}
        >
          {musicPlaying ? '🔊' : '🔇'}
        </button>
      </div>
    );
  };

  return (
    <div className={`ville-de-noel-effects ${!animationsEnabled ? 'no-animations' : ''}`}>
      <SnowEffect />
      <ChristmasMessage />
      <MusicController />
      
      {/* Message d'information si les animations sont désactivées */}
      {!animationsEnabled && effectiveSettings.hasSnowEffect && (
        <div className="accessibility-notice">
          <div className="accessibility-notice-content">
            <i className="fas fa-universal-access"></i>
            <span>Animations désactivées pour l'accessibilité</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default VilleDeNoelEffects;
